import React, { useEffect, useLayoutEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';

// Per-page tour definitions — each page teaches itself on first visit
const PAGE_TOURS = {
  '/dashboard': {
    storageKey: 'workintel_tour_v1_dashboard',
    steps: [
      {
        element: null,
        title: 'Dashboard',
        description: "Welcome to your Dashboard — the command centre for your team. Here you can monitor hours, capacity, and project burn rates at a glance.",
        icon: '📊',
      },
      {
        element: '[data-tour="member-filter"]',
        title: 'Team Member Filter',
        description: "Switch between your own dashboard and any individual team member's view — see their hours, projects, and workload as if you were them.",
        adminOnly: true,
      },
      {
        element: '[data-tour="announce-btn"]',
        title: 'Announcements',
        description: "Post a banner announcement visible to the entire team — use it for important updates, reminders, or alerts.",
        adminOnly: true,
      },
      {
        element: '[data-tour="theme-toggle"]',
        title: 'Dark / Light Mode',
        description: "Toggle between dark and light theme. Your preference is saved automatically.",
      },
      {
        element: '[data-tour="notif-btn"]',
        title: 'Notifications',
        description: "All your notifications live here — timesheet status updates, approvals, rejections, and system alerts.",
      },
      {
        element: '[data-testid="tab-overview"]',
        title: 'Overview Tab',
        description: "Shows this week's logged hours, billable vs non-billable split, active projects, and pending approvals.",
      },
      {
        element: '[data-testid="tab-capacity"]',
        title: 'Capacity Tab',
        description: "See how much of each team member's bandwidth is allocated. Spot overloaded or underutilised people instantly.",
        requiresReports: true,
      },
      {
        element: '[data-testid="tab-burnrate"]',
        title: 'Burn Rate Tab',
        description: "Track how fast each project is consuming its budget. Retainer and fixed-price projects show their monthly burn here.",
        requiresReports: true,
      },
      {
        element: '[data-testid="daily-hours-chart"]',
        title: 'Daily Hours Chart',
        description: "Visualises how many hours your team logged each day. Hover bars to see exact figures.",
      },
      {
        element: '[data-testid="recent-projects"]',
        title: 'Active Projects',
        description: "A quick-view list of all running projects with their logged hours and progress towards budget.",
      },
    ],
  },

  '/time-entry': {
    storageKey: 'workintel_tour_v1_time_entry',
    steps: [
      {
        element: null,
        title: 'Time Entry',
        description: "Log your hours here. Each row is a project and each column is a day of the week — just click a cell and type your hours.",
        icon: '⏱️',
      },
      {
        element: '[data-tour="time-entry-you-tab"]',
        title: 'Your Timesheet',
        description: "The You tab shows your own time entries for the week — fully editable. Log, edit, and submit your hours from here.",
        managerOnly: true,
      },
      {
        element: '[data-tour="time-entry-team-tab"]',
        title: 'Team View',
        description: "The Team tab shows a read-only view of every direct report's logged hours for the week — project rows, daily totals, and timesheet status all in one place.",
        managerOnly: true,
      },
      {
        element: '[data-testid="prev-week-btn"]',
        title: 'Week Navigation',
        description: "Jump between weeks using these arrows. You can log hours for past weeks or prepare ahead.",
      },
      {
        element: '[data-testid="add-project-select"]',
        title: 'Add a Project Row',
        description: "Select a project from this dropdown to add it to your grid. You can track time across multiple projects in the same week.",
      },
      {
        element: '[data-testid="time-grid"]',
        title: 'Time Grid',
        description: "Each row is a project. Click any cell and type hours for that day. Cells turn green once saved.",
      },
      {
        element: '[data-tour="time-entry-row"]',
        title: 'Project Row',
        description: "Each row shows one project across the week — enter hours per day, see the weekly total on the right, and remove the row with the trash icon if unused.",
      },
      {
        element: '[data-tour="note-icon"]',
        title: 'Work Notes',
        description: "Click this icon to add a work description, select a service vertical and work type, and flag any blockers. Required before submitting.",
      },
      {
        element: '[data-testid="allocation-summary"]',
        title: 'Hours Summary',
        description: "A running total of hours per project for this week — great for checking your distribution before submitting.",
      },
      {
        element: '[data-testid="submit-timesheet-btn"]',
        title: 'Submit for Approval',
        description: "When your hours are ready, click Submit to send the timesheet to your manager. You can still edit after submission until it's approved.",
      },
    ],
  },

  '/approvals': {
    storageKey: 'workintel_tour_v1_approvals',
    steps: [
      {
        element: null,
        title: 'Approvals',
        description: "Review and action your team's submitted timesheets. Each card represents one person's full working week.",
        icon: '✅',
      },
      {
        element: '[data-tour="approvals-stats"]',
        title: 'Status Summary',
        description: "These cards show how many timesheets are pending your review, already approved, or rejected for this period.",
      },
      {
        element: '[data-tour="approvals-tabs"]',
        title: 'Pending / Approved / Rejected',
        description: "Switch between tabs to see timesheets by status. Pending shows what needs your action, Approved shows signed-off weeks, and Rejected shows timesheets sent back for correction.",
      },
      {
        element: '[data-tour="approvals-member-filter"]',
        title: 'Filter by Member',
        description: "Narrow the list to a specific team member — select their name to see only their timesheets across Pending, Approved, and Rejected tabs. Defaults to All members.",
        managerOnly: true,
      },
      {
        element: '[data-tour="timesheet-list"]',
        title: 'Timesheet Cards',
        description: "Each card shows a team member's name, week dates, and total hours. Click to expand and see their full daily breakdown.",
      },
      {
        element: '[data-tour="approve-reject-actions"]',
        title: 'Approve or Reject',
        description: "Approve the full week with the green button, or use Reject to flag issues and write a reason that the employee will see.",
      },
      {
        element: '[data-tour="entry-flags"]',
        title: 'Flag Individual Entries',
        description: "Inside an expanded card, use ✓ / ✗ next to each row to pinpoint exactly which entries are problematic before rejecting.",
      },
    ],
  },

  '/projects': {
    storageKey: 'workintel_tour_v2_projects',
    steps: [
      {
        element: null,
        title: 'Projects',
        description: "Browse all client and internal projects here. Each project shows logged hours, budget consumption, billing type, and assigned team members.",
        icon: '📁',
      },
      {
        element: '[data-testid="create-project-btn"]',
        title: 'Create a Project',
        description: "Click here to add a new project. Set the name, billing type (Fixed Price, Retainer, or T&M), budget hours, dates, and assign team members with individual hour allocations.",
        adminOnly: true,
      },
      {
        element: '[data-testid="project-search"]',
        title: 'Search Projects',
        description: "Search projects by name to find what you need quickly across all your active and archived projects.",
      },
      {
        element: '[data-testid="status-filter"]',
        title: 'Status Filter',
        description: "Filter projects by status — Active, On Hold, Completed, or Archived. Combine with search to narrow results further.",
        adminOnly: true,
      },
      {
        element: '[data-tour="sort-options"]',
        title: 'Sort Projects',
        description: "Sort by name, hours logged, or date — ascending or descending. Useful for spotting the most active or recently created projects at the top.",
      },
      {
        element: '[data-tour="view-toggle"]',
        title: 'Grid / List View',
        description: "Switch between card grid and compact list view. List view shows more projects at once with hours, team size, and revenue in a single row.",
      },
      {
        element: '[data-tour="project-card-example"]',
        title: 'Project Card',
        description: "Each card shows the project name, client, status, department, and billing type. The progress bar tracks hours used against budget. Use the three-dot menu to edit, archive, or update the project status.",
      },
    ],
  },

  '/team': {
    storageKey: 'workintel_tour_v2_team',
    steps: [
      {
        element: null,
        title: 'Team',
        description: "Manage your people — add members, assign roles, set hourly rates, and configure the approval hierarchy.",
        icon: '👥',
      },
      {
        element: '[data-testid="add-member-btn"]',
        title: 'Add a Team Member',
        description: "Click to invite a new member. Fill in their name, email, department, role, and hourly rate. They'll receive a welcome email with login credentials automatically.",
      },
      {
        element: '[data-testid="team-search"]',
        title: 'Search Members',
        description: "Search by name or email to find a team member quickly across large organisations.",
      },
      {
        element: '[data-testid="department-filter"]',
        title: 'Department Filter',
        description: "Filter the member list by department to focus on a specific team — Development, Design, Marketing, and more.",
      },
      {
        element: '[data-tour="team-grid"]',
        title: 'Member Cards',
        description: "Each card shows role, department, hourly rate, and manager. Use the three-dot menu on each card to edit details, set a manager, deactivate, reset passwords, or remove the member.",
      },
      {
        element: '[data-tour="hierarchy-tab"]',
        title: 'Approval Hierarchy',
        description: "Switch here to see and configure the reporting structure — who reports to whom for timesheet approvals. The tree shows direct reports indented under each manager.",
      },
    ],
  },

  '/reports': {
    storageKey: 'workintel_tour_v2_reports',
    steps: [
      {
        element: null,
        title: 'Reports & Analytics',
        description: "Deep-dive analytics for your team's time data across three report types: Time & Utilization, Resource Allocation, and Project Allocation.",
        icon: '📈',
      },
      {
        element: '[data-tour="report-type-tabs"]',
        title: 'Report Types',
        description: "Switch between three views: Time & Utilization (hours logged per person and project), Resource Allocation (capacity vs allocation), and Project Allocation (budget burn per project).",
      },
      {
        element: '[data-tour="date-range-picker"]',
        title: 'Date Range',
        description: "Select any date range to scope the report. Use quick presets (This Week, This Month, Last 30 Days) or pick a custom range. All charts and tables update instantly.",
      },
      {
        element: '[data-tour="report-filters"]',
        title: 'Department & Member Filters',
        description: "Narrow the report by department or a specific team member. Combine filters to drill into exactly the data you need.",
      },
      {
        element: '[data-tour="export-buttons"]',
        title: 'Export Reports',
        description: "Download the full report as CSV, Excel, or PDF. All formats include Allocated Hrs and Remaining columns per employee-project pair. The Excel file also includes a dedicated Allocation Summary sheet.",
      },
      {
        element: '[data-tour="kpi-cards"]',
        title: 'Summary KPIs',
        description: "Four headline metrics for the selected period: Total Hours logged, Billable Hours, Utilization Rate, and Average Daily hours. These update whenever you change the date range or filters.",
      },
      {
        element: '[data-testid="daily-trend-chart"]',
        title: 'Daily Trend Chart',
        description: "Visualises how hours trended day by day across the selected period. Hover bars to see exact figures and spot low-productivity days at a glance.",
      },
      {
        element: '[data-testid="department-chart"]',
        title: 'Department Breakdown',
        description: "See which departments are logging the most hours — helps identify resource imbalances across the org.",
      },
      {
        element: '[data-tour="blockers-section"]',
        title: 'Blockers by Project',
        description: "All blockers your team flagged on their time entries — aggregated by project, with dates. Use this to spot recurring delivery risks before they escalate.",
      },
    ],
  },

  '/ai-insights': {
    storageKey: 'workintel_tour_v2_ai_insights',
    steps: [
      {
        element: null,
        title: 'AI Insights',
        description: "Let AI analyse your team's time data and surface utilisation patterns, risks, and recommendations — automatically.",
        icon: '✨',
      },
      {
        element: '[data-testid="refresh-insights-btn"]',
        title: 'Refresh Insights',
        description: "Click to generate a fresh AI analysis based on the latest time data. Insights are based on the current month's entries and update as new hours are logged.",
      },
      {
        element: '[data-tour="ai-metric-cards"]',
        title: 'Monthly Metrics',
        description: "Four headline numbers for the current month: total hours logged, billable hours, billable ratio percentage, and active project count.",
      },
      {
        element: '[data-tour="ai-trends"]',
        title: 'Trend Indicators',
        description: "Quick-read trend pills showing whether key metrics are moving up, down, or holding steady — useful for spotting direction at a glance without reading the full analysis.",
      },
      {
        element: '[data-testid="ai-analysis-card"]',
        title: 'AI Analysis',
        description: "A natural-language summary written by AI — highlights utilisation trends, capacity gaps, and project health risks with plain-English recommendations.",
      },
      {
        element: '[data-testid="department-breakdown"]',
        title: 'Department Breakdown',
        description: "Hours split by department with a percentage bar per team — quickly see which departments are carrying the most workload.",
      },
      {
        element: '[data-testid="project-utilization"]',
        title: 'At-Risk Projects',
        description: "Projects that are over-burning or nearing budget exhaustion. Red bars flag 100%+ utilisation; orange flags 80%+. Use this to intervene before a project goes over budget.",
      },
    ],
  },

  '/settings': {
    storageKey: 'workintel_tour_v1_settings',
    steps: [
      {
        element: null,
        title: 'Settings',
        description: "Manage your account profile, appearance, notifications, security, and admin system configuration all from here.",
        icon: '⚙️',
      },
      {
        element: '[data-tour="settings-profile"]',
        title: 'Profile',
        description: "View your name, email, role, department, job title, and employee ID. Click your avatar to upload a profile photo.",
      },
      {
        element: '[data-tour="settings-appearance"]',
        title: 'Appearance',
        description: "Toggle between dark and light mode. Your preference is saved automatically across sessions.",
      },
      {
        element: '[data-tour="settings-notifications"]',
        title: 'Notifications',
        description: "Control which notification types you receive — timesheet reminders, approval updates, and project assignments. View and clear recent notifications here too.",
      },
      {
        element: '[data-tour="settings-security"]',
        title: 'Security',
        description: "See your account status and membership date. Use the Change Password form here to update your credentials anytime.",
      },
      {
        element: '[data-tour="settings-system-config"]',
        title: 'System Configuration',
        description: "Admin-only section with two controls for managing time entry rules and automated email reminders. Changes take effect immediately after clicking Save Settings.",
        adminOnly: true,
      },
      {
        element: '[data-tour="settings-edit-window"]',
        title: 'Time Entry Edit Window',
        description: "Set how many days back team members can create or edit time entries. For example, 7 = last 7 days only. Set to 0 for no limit. Admins are never restricted.",
        adminOnly: true,
      },
      {
        element: '[data-tour="settings-reminder-config"]',
        title: 'Timesheet Approval Reminder',
        description: "Automatically email managers every Monday if they have unapproved timesheets. Set the number of days after submission (e.g. 7 = remind after 1 week) and the send time (e.g. 10:30 AM). Set days to 0 to disable reminders entirely.",
        adminOnly: true,
      },
    ],
  },

  '/service-catalog': {
    storageKey: 'workintel_tour_v2_service_catalog',
    steps: [
      {
        element: null,
        title: 'Service Catalog',
        description: "Define the work verticals and service types your team selects when logging time entries. Setting these up makes time data richer and more filterable.",
        icon: '🗂️',
      },
      {
        element: '[data-tour="verticals-section"]',
        title: 'Service Verticals',
        description: "Verticals are top-level service categories (e.g. Design, Development, Consulting). Each vertical gets a colour for easy identification. Use the edit and delete icons on each row to manage them.",
      },
      {
        element: '[data-tour="add-vertical-btn"]',
        title: 'Add a Vertical',
        description: "Click here to create a new service vertical. Give it a name, an optional description, and choose a colour. It will immediately appear in the time entry dropdown.",
      },
      {
        element: '[data-tour="work-types-section"]',
        title: 'Work Types',
        description: "Work types are specific activities within a vertical (e.g. Strategy, Execution, Reporting, Client Call). Team members select one when logging time to indicate what kind of work they did.",
      },
      {
        element: '[data-tour="add-worktype-btn"]',
        title: 'Add a Work Type',
        description: "Click here to create a new work type. Give it a name and optional description — it appears alongside verticals in the time entry notes panel.",
      },
    ],
  },
};

const PAD = 10;
const CARD_W = 300;

function getRect(selector) {
  if (!selector) return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  // Scroll element into view so it's always visible before measuring
  el.scrollIntoView({ behavior: 'instant', block: 'center', inline: 'nearest' });
  return el.getBoundingClientRect();
}

export function AdminTour({ pathname, manualTrigger, isAdmin = false, isManager = false, canViewReports = false }) {
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const [cardPos, setCardPos] = useState(null);
  const cardRef = useRef(null);

  const initTour = useCallback((force = false) => {
    const tourDef = PAGE_TOURS[pathname];
    if (!tourDef) { setSteps(null); return; }
    if (!force && localStorage.getItem(tourDef.storageKey)) { setSteps(null); return; }
    const filtered = tourDef.steps.filter(s =>
      (!s.adminOnly || isAdmin) &&
      (!s.managerOnly || isManager || isAdmin) &&
      (!s.requiresReports || canViewReports)
    );
    if (!filtered.length) { setSteps(null); return; }
    setStep(0);
    setSteps(filtered);
    window.dispatchEvent(new CustomEvent('workintel:tour:start', { detail: { pathname } }));
  }, [pathname, isAdmin, isManager, canViewReports]);

  // Auto-start when navigating to a new page
  useEffect(() => {
    setSteps(null);
    const t = setTimeout(() => initTour(false), 700);
    return () => clearTimeout(t);
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Manual restart via ? button
  useEffect(() => {
    if (manualTrigger > 0) {
      setStep(0);
      initTour(true);
    }
  }, [manualTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  const current = steps ? steps[step] : null;
  const isFirst = step === 0;
  const isLast = steps ? step === steps.length - 1 : false;

  const reposition = useCallback(() => {
    if (!current) return;
    const r = getRect(current.element);
    setRect(r);
    if (!r) { setCardPos(null); return; }

    const ch = cardRef.current ? cardRef.current.offsetHeight : 220;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left, top;

    const rightSpace  = vw - r.right;
    const leftSpace   = r.left;
    const topSpace    = r.top;

    if (rightSpace >= CARD_W + 32) {
      // Place right of element
      left = r.right + 16;
      top  = r.top + r.height / 2 - ch / 2;
    } else if (leftSpace >= CARD_W + 32) {
      // Place left of element
      left = r.left - CARD_W - 16;
      top  = r.top + r.height / 2 - ch / 2;
    } else if (topSpace >= ch + 32) {
      // Wide element — place above, horizontally centred
      left = r.left + r.width / 2 - CARD_W / 2;
      top  = r.top - ch - 16;
    } else {
      // Fall back to below, horizontally centred
      left = r.left + r.width / 2 - CARD_W / 2;
      top  = r.bottom + 16;
    }

    // Clamp to viewport
    top  = Math.max(16, Math.min(top,  vh - ch - 16));
    left = Math.max(16, Math.min(left, vw - CARD_W - 16));

    setCardPos({ top, left });
  }, [current]);

  useLayoutEffect(() => {
    if (!steps) return;
    reposition();
    window.addEventListener('resize', reposition);
    return () => window.removeEventListener('resize', reposition);
  }, [steps, step, reposition]);

  // Lock scroll on the main content container while tour is open
  useEffect(() => {
    if (!steps) return;
    const container = document.getElementById('tour-scroll-container');
    if (!container) return;
    const prev = container.style.overflow;
    container.style.overflow = 'hidden';
    return () => { container.style.overflow = prev; };
  }, [steps]);

  const close = useCallback(() => {
    const tourDef = PAGE_TOURS[pathname];
    if (tourDef) localStorage.setItem(tourDef.storageKey, 'true');
    window.dispatchEvent(new CustomEvent('workintel:tour:end'));
    setSteps(null);
  }, [pathname]);

  const next = () => (isLast ? close() : setStep(s => s + 1));
  const prev = () => !isFirst && setStep(s => s - 1);

  useEffect(() => {
    const onKey = (e) => {
      if (!steps) return;
      if (e.key === 'ArrowRight' || e.key === 'Enter') next();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [steps, step]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!steps || !current) return null;

  const isCentered = !current.element || !rect;
  const progress = ((step + 1) / steps.length) * 100;

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 99990, pointerEvents: 'none' }}>

      {/* Full-screen click + scroll blocker (transparent, sits under spotlight) */}
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 99991, pointerEvents: 'all' }}
        onClick={close}
      />

      {/* Backdrop / spotlight */}
      {isCentered ? (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)', pointerEvents: 'none', zIndex: 99992 }}
        />
      ) : rect ? (
        <div
          style={{
            position: 'fixed',
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            borderRadius: 10,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)',
            outline: '2px solid hsl(var(--primary))',
            outlineOffset: 1,
            transition: 'top 0.25s ease, left 0.25s ease, width 0.25s ease, height 0.25s ease',
            pointerEvents: 'none',
            zIndex: 99992,
          }}
        />
      ) : null}

      {/* Tour card */}
      <div
        ref={cardRef}
        onClick={e => e.stopPropagation()}
        style={isCentered || !cardPos ? {
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: CARD_W + 60,
          zIndex: 99999,
          pointerEvents: 'all',
        } : {
          position: 'fixed',
          top: cardPos.top,
          left: cardPos.left,
          width: CARD_W,
          zIndex: 99999,
          pointerEvents: 'all',
          transition: 'top 0.25s ease, left 0.25s ease',
        }}
        className="rounded-2xl bg-card border border-border shadow-2xl overflow-hidden"
      >
        {/* Progress bar */}
        <div className="h-0.5 bg-muted">
          <div className="h-full bg-primary transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-3.5 pb-0">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center">
              <span className="text-primary text-[10px] font-bold leading-none">{step + 1}</span>
            </div>
            <span className="text-[11px] text-muted-foreground font-medium tracking-wide uppercase">
              {step + 1} / {steps.length}
            </span>
          </div>
          <button
            onClick={close}
            className="w-6 h-6 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 pt-2.5 pb-3">
          <div className="flex items-start gap-2.5">
            {current.icon && (
              <span className="text-xl leading-none mt-0.5 flex-shrink-0">{current.icon}</span>
            )}
            <div>
              <h3 className="font-semibold text-foreground text-sm leading-snug mb-1">{current.title}</h3>
              <p className="text-[13px] text-muted-foreground leading-relaxed">{current.description}</p>
            </div>
          </div>
        </div>

        {/* Step dots */}
        <div className="px-4 pb-3 flex gap-1">
          {steps.map((_, i) => (
            <button
              key={i}
              onClick={() => setStep(i)}
              className={`h-1 rounded-full transition-all duration-300 ${
                i === step ? 'bg-primary w-5' : i < step ? 'bg-primary/40 w-2' : 'bg-muted w-2'
              }`}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="px-4 pb-4 flex items-center justify-between">
          <button
            onClick={prev}
            disabled={isFirst}
            className="flex items-center gap-1 text-[13px] text-muted-foreground hover:text-foreground disabled:opacity-0 disabled:pointer-events-none transition-colors"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-center gap-2">
            {!isLast && (
              <button onClick={close} className="text-[12px] text-muted-foreground hover:text-foreground transition-colors">
                Skip
              </button>
            )}
            <button
              onClick={next}
              className="flex items-center gap-1.5 bg-primary text-primary-foreground text-[13px] font-medium px-3.5 py-1.5 rounded-lg hover:opacity-90 transition-opacity"
            >
              {isLast ? 'Finish' : 'Next'}
              {!isLast && <ChevronRight className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
